import AsyncStorage from "@react-native-async-storage/async-storage";
import {Platform} from "react-native";
import {showPrintErrorDialog} from "./printerLibrary/printer";
import {getBigReceiptData, getBigTerminalData} from "./printerLibrary/oldPrinter";
import {getUserInfo} from "./storage";
import {completePrintJob, registerPrintJob} from './PrintQueueMonitor';
import i18n from "../../i18n";
import {safeJsonParse} from './safeJsonParse';

const hasPrinterAddress = (printer = {}) => {
    return Boolean(printer?.ip || printer?.address || printer?.macAddress || printer?._nativeDevice?.address);
};

let printQueue = Promise.resolve();

const enqueuePrintJob = (label, job) => {
    const queuedJobId = registerPrintJob(label);
    const queuedJob = printQueue
        .catch(() => undefined)
        .then(() => job());

    printQueue = queuedJob.catch(() => undefined);
    return queuedJob.finally(() => {
        completePrintJob(queuedJobId);
    });
};

const getConfiguredKitchenPrinter = async () => {
    const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
    const printerEvents = safeJsonParse(storedPrinterEvents, []);
    const terminalEvents = printerEvents.filter(event => event.type === "terminal");

    for (const event of terminalEvents) {
        const printers = Array.isArray(event?.printers) ? event.printers : [];
        const printer = printers.find(hasPrinterAddress);
        if (printer) {
            return printer;
        }
    }

    return null;
};

const getConfiguredReceiptPrinter = async () => {
    const printerDevice = await AsyncStorage.getItem('printerDevice');
    const parsedPrinterDevice = safeJsonParse(printerDevice, null);

    if (hasPrinterAddress(parsedPrinterDevice)) {
        return parsedPrinterDevice;
    }

    const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
    const printerEvents = safeJsonParse(storedPrinterEvents, []);
    const receiptEvent = printerEvents.find(event => event.type === "receipt" && Array.isArray(event?.printers));

    return receiptEvent?.printers?.find(hasPrinterAddress) || null;
};

const resolvePrintOutcomeError = (result, fallbackMessage) => {
    if (result?.skipped === true) {
        return null;
    }

    if (result === true) {
        return null;
    }

    if (result?.error instanceof Error) {
        return result.error;
    }

    if (result && result.error) {
        return new Error(result.error?.message || fallbackMessage);
    }

    if (result === false || typeof result === 'undefined') {
        return new Error(fallbackMessage);
    }

    return null;
};

export async function hasAnyConfiguredPrinter() {
    const [receiptPrinter, kitchenPrinter] = await Promise.all([
        getConfiguredReceiptPrinter(),
        getConfiguredKitchenPrinter(),
    ]);

    return Boolean(receiptPrinter || kitchenPrinter);
}

export async function RawBTPrintKitchen(order, items, options = {}) {
    if (Platform.OS !== 'android') return;
    const shouldShowErrorDialog = options?.showErrorDialog !== false;

    return enqueuePrintJob(i18n.t('print_queue_kitchen_job'), async () => {
        try {
            const printer = await getConfiguredKitchenPrinter();
            if (!printer) {
                return { skipped: true, reason: 'no_kitchen_printer_configured' };
            }

            // Import the background task trigger function
            const BackgroundTasks = require('./BackgroundTasks');
            const triggerKitchenPrinting = BackgroundTasks?.triggerKitchenPrinting;

            if (typeof triggerKitchenPrinting !== 'function') {
                throw new Error('Kitchen printing service is not available');
            }

            // Use the background task to print to kitchen printers
            const result = await triggerKitchenPrinting(order, items, { showAlert: shouldShowErrorDialog, printedTargets: options?.printedTargets || null });
            const printError = resolvePrintOutcomeError(result, 'Kitchen printing failed');

            if (printError) {
                throw printError;
            }

            return true;
        } catch (e) {
            console.error(e);

            // Handle synchronous errors by getting printer info asynchronously
            if (shouldShowErrorDialog) {
                AsyncStorage.getItem('printerEvents')
                    .then(storedPrinterEvents => {
                        const printerEvents = safeJsonParse(storedPrinterEvents, []);
                        let printer = { ip: '', macAddress: '' }; // Default fallback

                        if (printerEvents.length > 0) {
                            const terminalEvents = printerEvents.filter(event => event.type === "terminal");
                            if (terminalEvents.length > 0 && terminalEvents[0].printers && terminalEvents[0].printers.length > 0) {
                                printer = terminalEvents[0].printers[0];
                            }
                        }

                        // Get data for the template
                        Promise.all([
                            getUserInfo(),
                            AsyncStorage.getItem("restaurant")
                        ]).then(([userInfo, restaurantData]) => {
                            showPrintErrorDialog(
                                getBigTerminalData(order, safeJsonParse(restaurantData, {}), userInfo),
                                printer,
                                true,
                                true
                            );
                        });
                    })
                    .catch(asyncError => {
                        console.error('Error getting printer info:', asyncError);
                        // Fallback to dummy printer if everything fails
                        const dummyPrinter = { ip: '', macAddress: '' };
                        showPrintErrorDialog(getBigTerminalData(order, {}, {}), dummyPrinter, true, true);
                    });
            }
            return false;
        }
    });
}

export async function RawBTPrintTicket(order, options = {}) {
    if (Platform.OS !== 'android') return;
    const shouldShowErrorDialog = options?.showErrorDialog !== false;

    return enqueuePrintJob(i18n.t('print_queue_receipt_job'), async () => {
        try {
            const configuredPrinter = await getConfiguredReceiptPrinter();
            if (!configuredPrinter) {
                return { skipped: true, reason: 'no_receipt_printer_configured' };
            }

            // Import the background task trigger function
            const BackgroundTasks = require('./BackgroundTasks');
            const triggerReceiptPrinting = BackgroundTasks?.triggerReceiptPrinting;

            if (typeof triggerReceiptPrinting !== 'function') {
                throw new Error('Receipt printing service is not available');
            }

            // Use the background task to print the receipt
            const result = await triggerReceiptPrinting(order, { showAlert: shouldShowErrorDialog, printedTargets: options?.printedTargets || null });
            const printError = resolvePrintOutcomeError(result, 'Receipt printing failed');

            if (printError) {
                throw printError;
            }

            return true;
        } catch (e) {
            console.error(e);

            // Handle synchronous errors by getting printer info asynchronously
            if (shouldShowErrorDialog) {
                Promise.all([
                    AsyncStorage.getItem('printerDevice'),
                    AsyncStorage.getItem('printerEvents'),
                    getUserInfo(),
                    AsyncStorage.getItem("restaurant"),
                    AsyncStorage.getItem("restaurantPlace")
                ])
                .then(([printerDevice, storedPrinterEvents, userInfo, restaurantData, restaurantPlaceData]) => {
                    // Try to get the main printer device first
                    let printer = safeJsonParse(printerDevice, null);

                    // If no main printer, try to get from printer events
                    if (!printer) {
                        const printerEvents = safeJsonParse(storedPrinterEvents, []);

                        if (printerEvents.length > 0) {
                            const receiptEvent = printerEvents.find(event => event.type === "receipt");
                            if (receiptEvent && receiptEvent.printers && receiptEvent.printers.length > 0) {
                                printer = receiptEvent.printers[0];
                            }
                        }
                    }

                    // If still no printer, use default empty values
                    if (!printer) {
                        printer = { ip: '', macAddress: '' };
                    }

                    // Show the custom error dialog with retry option using the actual printer
                    showPrintErrorDialog(
                        getBigReceiptData(
                            order,
                            safeJsonParse(restaurantData, {}),
                            safeJsonParse(restaurantPlaceData, {}),
                            userInfo
                        ),
                        printer,
                        true,
                        true
                    );
                })
                .catch(asyncError => {
                    console.error('Error getting printer info:', asyncError);
                    // Fallback to dummy printer if everything fails
                    const dummyPrinter = { ip: '', macAddress: '' };
                    showPrintErrorDialog(getBigReceiptData(order, {}, {}, {}), dummyPrinter, true, true);
                });
            }
            return false;
        }
    });
}
